import { catalogueItem, sizeAssumed, type BodyBuild, type Dress, type Pose } from '@mawkeb/shared/catalogue';
import { placedItem, placedSize, seatCount, type Layout, type Placement, type PropPlacement, type RowsPlacement } from '@mawkeb/shared/layout';
import { formatLength, LENGTH_HINT, parseLength, type UnitSystem } from '@mawkeb/shared/units';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { align, breakRows, distribute, duplicate, fillSeated, freeSeats, moveBy, onWall, remove, rotateBy, setLocked, setRows, STEP, wrap } from '../engine/editing';
import { ImageProblem, keepImage } from '../state/images';
import { layoutState, useLayout } from '../state/layout';
import { useVenue, venue } from '../state/store';
import { Copy, Lock, Rotate, Trash } from './icons';
import { viewer } from './Stage';

const commit = (next: Layout) => layoutState().commit(next);

function patchProp(layout: Layout, id: string, change: (p: PropPlacement) => PropPlacement): Layout {
  return { ...layout, placements: layout.placements.map((p) => (p.id === id && p.kind === 'prop' ? change(p) : p)) };
}

function LengthInput({ label, value, min, max, units, onCommit }: { label: string; value: number; min: number; max: number; units: UnitSystem; onCommit: (m: number) => void }) {
  const show = (m: number) => formatLength(m, units, { style: 'ft-in' });
  const [text, setText] = useState(show(value));
  const [bad, setBad] = useState<string | null>(null);
  const id = useId();
  useEffect(() => setText(formatLength(value, units, { style: 'ft-in' })), [value, units]);
  const done = () => {
    const m = parseLength(text, units === 'metric' ? 'm' : 'ft');
    if (m === null) return setBad(LENGTH_HINT);
    if (m < min || m > max) return setBad(`Between ${formatLength(min, units)} and ${formatLength(max, units)}`);
    setBad(null);
    if (Math.abs(m - value) > 1e-6) onCommit(m);
    else setText(show(value));
  };
  return (
    <div className="row field-row">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="field val" value={text} aria-invalid={!!bad} title={bad ?? undefined} onChange={(e) => setText(e.target.value)} onBlur={done} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />
      {bad && <p className="hint bad">{bad}</p>}
    </div>
  );
}

function CountInput({ label, value, min, max, onCommit }: { label: string; value: number; min: number; max: number; onCommit: (n: number) => void }) {
  const [text, setText] = useState(String(value));
  const id = useId();
  useEffect(() => setText(String(value)), [value]);
  const done = () => {
    const n = Math.round(Number(text));
    if (!Number.isFinite(n) || n < min || n > max) return setText(String(value));
    if (n !== value) onCommit(n);
  };
  return (
    <div className="row field-row">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="field val" inputMode="numeric" value={text} onChange={(e) => setText(e.target.value)} onBlur={done} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />
    </div>
  );
}

function Choice<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { id: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="row choice-row">
      <span>{label}</span>
      <div className="seg small" role="group" aria-label={label}>
        {options.map((o) => (
          <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RowsFields({ p, layout, units }: { p: RowsPlacement; layout: Layout; units: UnitSystem }) {
  const set = (patch: Parameters<typeof setRows>[2]) => commit(setRows(layout, p.id, patch));
  const facing = Math.abs(Math.cos(p.aim.yaw)) > 0.5 ? (Math.cos(p.aim.yaw) > 0 ? 'stage' : 'back') : 'other';
  return (
    <>
      <CountInput label="Chairs in the row" value={p.perRow} min={1} max={60} onCommit={(perRow) => set({ perRow })} />
      <LengthInput label="Gap between chairs" value={p.gap} min={0} max={1} units={units} onCommit={(gap) => set({ gap })} />
      <CountInput label="Rows" value={p.rows} min={1} max={60} onCommit={(rows) => set({ rows })} />
      <LengthInput label="Row spacing" value={p.spacing} min={0.5} max={3} units={units} onCommit={(spacing) => set({ spacing })} />
      <LengthInput label="Centre aisle" value={p.aisle} min={0} max={5} units={units} onCommit={(aisle) => set({ aisle })} />
      <Choice
        label="Facing"
        value={facing}
        options={[{ id: 'stage', label: 'Stage' }, { id: 'back', label: 'Back wall' }, ...(facing === 'other' ? [{ id: 'other' as const, label: 'Turned' }] : [])]}
        onChange={(v) => v !== 'other' && commit(rotateBy(layout, new Set([p.id]), (v === 'stage' ? 0 : Math.PI) - p.aim.yaw))}
      />
      <div className="row">
        <span>Seats</span>
        <span className="val readout">{seatCount(p)}</span>
      </div>
      <button type="button" className="secondary wide" onClick={() => {
        const r = breakRows(layout, p.id);
        commit(r.layout);
        layoutState().select(r.ids);
      }}>
        Break into single chairs
      </button>
    </>
  );
}

function PropFields({ p, layout, units }: { p: PropPlacement; layout: Layout; units: UnitSystem }) {
  const item = catalogueItem(p.item);
  if (!item) return null;
  const [w, d, h] = placedSize(p, item);
  const resize = (size: [number, number, number]) => commit(patchProp(layout, p.id, (q) => ({ ...q, size })));
  if (item.make === 'carpet') {
    return <LengthInput label="Length" value={d} min={1} max={item.size[1]} units={units} onCommit={(m) => resize([w, m, h])} />;
  }
  if (item.make === 'panel') {
    const shown = placedItem(p, item);
    const aspect = p.image ? p.image.height / p.image.width : null;
    const keep = aspect !== null && p.specs.keepAspect !== false;
    const onWallNow = shown.mount === 'wall';
    const hang = shown.hangAt ?? 0;
    const choosePicture = async (file: File | undefined) => {
      if (!file) return;
      try {
        const ref = await keepImage(file);
        const l = layoutState().layout;
        commit(patchProp(l, p.id, (q) => ({ ...q, image: ref, size: [w, d, (w * ref.height) / ref.width], specs: { ...q.specs, keepAspect: true } })));
      } catch (e) {
        venue().toast(e instanceof ImageProblem ? e.message : 'That picture couldn’t be kept on this device. Try again.', { tone: 'bad' });
      }
    };
    const setMount = (mount: 'wall' | 'stand') => {
      if (mount === shown.mount) return;
      // keep it at the same height: a wall gives the bottom edge's height, a stand the top edge's
      const hangAt = mount === 'stand' ? hang + h : Math.max(0, hang - h);
      let next = patchProp(layout, p.id, (q) => ({ ...q, mount, hangAt }));
      const model = venue().model;
      const v = viewer();
      if (mount === 'wall' && model && v) next = moveBy(next, new Set([p.id]), 0, 0, v.surfaces, model);
      commit(next);
    };
    return (
      <>
        <div className="row">
          <span>Picture</span>
          <label className="secondary file-pick">
            {p.image ? 'Change…' : 'Choose…'}
            <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => void choosePicture(e.target.files?.[0])} />
          </label>
        </div>
        {p.image && <p className="hint">{p.image.name}</p>}
        <LengthInput label="Width" value={w} min={0.05} max={15} units={units} onCommit={(m) => resize([m, d, keep && aspect ? m * aspect : h])} />
        <LengthInput label="Height" value={h} min={0.05} max={8} units={units} onCommit={(m) => commit(patchProp(layout, p.id, (q) => ({ ...q, size: [w, d, m], specs: { ...q.specs, keepAspect: false } })))} />
        <Choice label="Hangs" value={onWallNow ? 'wall' : 'stand'} options={[{ id: 'wall', label: 'On a wall' }, { id: 'stand', label: 'On a stand' }]} onChange={setMount} />
        <LengthInput label={onWallNow ? 'Bottom edge at' : 'Top edge at'} value={hang} min={0} max={8} units={units} onCommit={(hangAt) => commit(patchProp(layout, p.id, (q) => ({ ...q, hangAt })))} />
      </>
    );
  }
  if (item.make === 'person' && p.person) {
    const person = p.person;
    const setPerson = (patch: Partial<typeof person>) => commit(patchProp(layout, p.id, (q) => ({ ...q, person: { ...person, ...patch } })));
    return (
      <>
        <Choice<Dress> label="Dress" value={person.dress} options={[{ id: 'men', label: 'Man' }, { id: 'women', label: 'Woman' }]} onChange={(dress) => setPerson({ dress })} />
        <Choice<Pose> label="Pose" value={person.pose === 'minbar' || person.pose === 'platform' ? 'floor' : person.pose} options={[{ id: 'standing', label: 'Standing' }, { id: 'floor', label: 'Seated' }, { id: 'chair', label: 'On a chair' }]} onChange={(pose) => setPerson({ pose })} />
        <LengthInput label="Height" value={person.height} min={0.9} max={2.2} units={units} onCommit={(height) => setPerson({ height })} />
        <Choice<BodyBuild> label="Build" value={person.build} options={[{ id: 'slim', label: 'Slim' }, { id: 'medium', label: 'Medium' }, { id: 'broad', label: 'Broad' }]} onChange={(build) => setPerson({ build })} />
      </>
    );
  }
  return null;
}

function FillPeople({ chosen, layout }: { chosen: Placement[]; layout: Layout }) {
  const fillable = chosen.some((p) => p.kind === 'rows' || catalogueItem(p.item)?.make === 'carpet');
  const capacity = fillable ? freeSeats(layout, new Set(chosen.map((p) => p.id))) : 0;
  const [count, setCount] = useState(capacity);
  const [dress, setDress] = useState<Dress>('men');
  useEffect(() => setCount(capacity), [capacity]);
  if (!fillable) return null;
  if (!capacity) {
    return (
      <div className="fill">
        <h3>Seated people</h3>
        <p className="hint">Every place here has someone in it.</p>
      </div>
    );
  }
  const fill = () => {
    const r = fillSeated(layout, new Set(chosen.map((p) => p.id)), Math.min(count, capacity), dress);
    commit(r.layout);
    venue().toast(`Seated ${r.ids.length} ${r.ids.length === 1 ? 'person' : 'people'}.`);
  };
  return (
    <div className="fill">
      <h3>Seated people</h3>
      <CountInput label={`How many (room for ${capacity})`} value={count} min={1} max={capacity} onCommit={setCount} />
      <Choice<Dress> label="Who" value={dress} options={[{ id: 'men', label: 'Men' }, { id: 'women', label: 'Women' }]} onChange={setDress} />
      <button type="button" className="secondary wide" onClick={fill}>
        Fill with {Math.min(count, capacity)} seated {dress}
      </button>
    </div>
  );
}

/** A placement's turn as a lead reads it: degrees clockwise from facing the stage. */
function degrees(yaw: number): number {
  const d = Math.round((((-yaw * 180) / Math.PI) % 360 + 360) % 360);
  return d === 360 ? 0 : d;
}

function AngleInput({ p, layout }: { p: Placement; layout: Layout }) {
  const [text, setText] = useState(`${degrees(p.aim.yaw)}°`);
  useEffect(() => setText(`${degrees(p.aim.yaw)}°`), [p.aim.yaw]);
  const done = () => {
    const d = Number(text.replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(d) || text.trim() === '') return setText(`${degrees(p.aim.yaw)}°`);
    const by = wrap((-d * Math.PI) / 180 - p.aim.yaw);
    if (Math.abs(by) > 1e-6) commit(rotateBy(layout, new Set([p.id]), by));
    else setText(`${degrees(p.aim.yaw)}°`);
  };
  return <input className="field angle" aria-label="Turn, degrees clockwise from facing the stage" inputMode="decimal" value={text} onChange={(e) => setText(e.target.value)} onBlur={done} onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()} />;
}

function Actions({ chosen, layout, compact }: { chosen: Placement[]; layout: Layout; compact?: boolean }) {
  const ids = new Set(chosen.map((p) => p.id));
  const locked = chosen.every((p) => p.locked);
  const turnable = chosen.some((p) => !onWall(p));
  const items: { label: string; icon: ReactNode; run: () => void; danger?: boolean; hidden?: boolean }[] = [
    { label: 'Turn', icon: <Rotate />, run: () => commit(rotateBy(layout, ids, -STEP)), hidden: !compact || !turnable },
    {
      label: 'Copy',
      icon: <Copy />,
      run: () => {
        const r = duplicate(layout, ids, 0.45, -0.45);
        commit(r.layout);
        layoutState().select(r.ids);
      },
    },
    { label: locked ? 'Unlock' : 'Lock', icon: <Lock open={locked} />, run: () => commit(setLocked(layout, ids, !locked)) },
    { label: 'Delete', icon: <Trash />, run: () => commit(remove(layout, ids)), danger: true },
  ];
  return (
    <div className={compact ? 'tool-buttons' : 'acts'}>
      {items
        .filter((i) => !i.hidden)
        .map((i) => (
          <button key={i.label} type="button" className={i.danger ? 'del' : undefined} onClick={i.run} title={i.label}>
            {compact && i.icon}
            {i.label === 'Copy' && !compact ? 'Duplicate' : i.label}
          </button>
        ))}
    </div>
  );
}

function Details({ chosen, layout, units }: { chosen: Placement[]; layout: Layout; units: UnitSystem }) {
  const single = chosen.length === 1 ? chosen[0]! : null;
  const item = single ? catalogueItem(single.item) : null;
  const ids = new Set(chosen.map((p) => p.id));
  const title = single ? (single.kind === 'rows' ? 'Rows of chairs' : (item?.name ?? 'Prop')) : `${chosen.length} items`;
  const turnable = chosen.some((p) => !onWall(p));
  let lead: ReactNode = null;
  if (single && item) {
    const size = single.kind === 'prop' ? placedSize(single, item) : item.size;
    const f = (m: number) => formatLength(m, units);
    const text = single.kind === 'rows' ? item.name : item.make === 'panel' ? `${f(size[0])} × ${f(size[2])}` : item.make === 'person' ? '' : `${f(size[0])} × ${f(size[1])}, ${f(size[2])} high`;
    lead = (
      <p className="lead">
        {text}
        {sizeAssumed(item) && <span className="tag">size assumed</span>}
      </p>
    );
  }
  return (
    <>
      <h2>{title}</h2>
      {lead}
      {single?.kind === 'rows' && <RowsFields p={single} layout={layout} units={units} />}
      {single?.kind === 'prop' && <PropFields p={single} layout={layout} units={units} />}
      {turnable && (
        <div className="row choice-row">
          <span>Turn</span>
          <div className="seg small" role="group" aria-label="Turn">
            <button type="button" onClick={() => commit(rotateBy(layout, ids, STEP))} title="Turn left 15° (Shift+R; Alt for 1°)">
              ↺ 15°
            </button>
            {single ? <AngleInput p={single} layout={layout} /> : <span className="val readout">—</span>}
            <button type="button" onClick={() => commit(rotateBy(layout, ids, -STEP))} title="Turn right 15° (R; Alt for 1°)">
              15° ↻
            </button>
          </div>
        </div>
      )}
      {chosen.length >= 2 && (
        <div className="row choice-row">
          <span>Line up</span>
          <div className="seg small" role="group" aria-label="Line up">
            {(['left', 'middle-x', 'right', 'back', 'middle-y', 'front'] as const).map((edge) => (
              <button key={edge} type="button" onClick={() => commit(align(layout, ids, edge))}>
                {{ left: 'Left', right: 'Right', back: 'Back', front: 'Front', 'middle-x': 'Middle', 'middle-y': 'Middle' }[edge]}
              </button>
            ))}
          </div>
        </div>
      )}
      {chosen.length >= 3 && (
        <div className="row choice-row">
          <span>Space evenly</span>
          <div className="seg small" role="group" aria-label="Space evenly">
            <button type="button" onClick={() => commit(distribute(layout, ids, 'x'))}>
              Across
            </button>
            <button type="button" onClick={() => commit(distribute(layout, ids, 'y'))}>
              Along
            </button>
          </div>
        </div>
      )}
      <FillPeople chosen={chosen} layout={layout} />
      {chosen.some((p) => p.locked) && <p className="hint">Locked: it stays put until it is unlocked.</p>}
    </>
  );
}

export function Inspector() {
  const selection = useLayout((s) => s.selection);
  const layout = useLayout((s) => s.layout);
  const units = useVenue((s) => s.units);
  const tool = useVenue((s) => s.tool);
  const library = useVenue((s) => s.library);
  const [more, setMore] = useState(false);
  useEffect(() => setMore(false), [selection, library]);
  const chosen = layout.placements.filter((p) => selection.includes(p.id));
  if (!chosen.length || tool !== 'none') return null;
  return (
    <>
      <aside className="panel inspector side only-laptop" aria-label="Selected">
        <Details chosen={chosen} layout={layout} units={units} />
        <Actions chosen={chosen} layout={layout} />
      </aside>
      {/* on a phone the library is a sheet in the same place: the selection steps aside while it is open */}
      {!library && (
        <div className="phone-edit only-phone">
          {more && (
            <div className="panel inspector sheet" aria-label="Selected">
              <Details chosen={chosen} layout={layout} units={units} />
            </div>
          )}
          <div className="panel tool" role="toolbar" aria-label="Selected">
            <Actions chosen={chosen} layout={layout} compact />
            <button type="button" aria-expanded={more} onClick={() => setMore(!more)} title="More">
              <span className="more-dots" aria-hidden="true">
                •••
              </span>
              More
            </button>
          </div>
        </div>
      )}
    </>
  );
}
